import {isEmpty as _isEmpty, isEqual as _isEqual, get as _get} from 'lodash';

import {withApollo} from 'react-apollo';
import {
    getCommonProductsWithBookingSupport,
    getRightScopesWithMediaRightsForMatches,
} from '@modules/events/views/common/EventsMultipleActions';
import {
    formatClipProvidersForSubmit,
    getDefaultClipProvidersValues,
    getClipProviderFromPropertyLicence,
    normalizeClipProvidersValues,
    prefillCoverage,
    resolveClipProvidersDefaults,
    setClipProvidersInForm,
    hasArgosJobScheduling,
    hasSpiideoIntegration,
    transformProductsForCoverageDropdown,
    updateClipProvidersFieldBasedOnPreset,
} from '@modules/events/utils/formHelper';
import {refetchQueryByName} from '@utils/apollo';

import EventForm from './EventForm';

export class EventsMultipleCreationForm extends EventForm {
    lastClipProvidersCoverageKey = null;

    componentDidMount() {
        super.componentDidMount();

        this.setCreateErrorCallback(() => {
            this.props.Modal.setModal({isVisible: false});
        });

        this.setCreateSuccessCallback(() => {
            refetchQueryByName(this.props.formParams.queryForRefetch);
        });

        this.setOnChangeCallback({
            property_licence: (data) => {
                this.setField('coverage', {
                    defaultValue: [],
                    timestamp: Date.now(),
                });

                this.setField('event_preset', {
                    defaultValue: null,
                    timestamp: Date.now(),
                });

                const hasArgos = hasArgosJobScheduling(
                    this.props.formParams.propertyLicences,
                    data.value
                );

                this.setField('argos_job_scheduling', {
                    hidden: !hasArgos,
                    checked: false,
                    defaultValue: false,
                });

                if (!hasArgos) {
                    this.props.change('argos_job_scheduling', false);
                }

                const hasSpiideo = hasSpiideoIntegration(
                    this.props.formParams.propertyLicences,
                    data.value
                );

                this.setField('spiideo_integration', {
                    hidden: !hasSpiideo,
                    checked: false,
                    defaultValue: false,
                });

                if (!hasSpiideo) {
                    this.props.change('spiideo_integration', false);
                }

                this.getPresetsForPropertyLicence(data.value);
                this.prepareCoverageField(this.props.formParams.matches, data.value);
            },
        });

        this.setField('property_licence', {
            options: this.props.formParams.propertyLicencesOptions,
        });

        this.setField('event_status', {
            options: this.props.formParams.eventStatuses,
        });

        this.setField('clip_providers', {
            options: this.props.formParams.clipProviders,
            eventContentTypes: this.props.formParams.eventContentTypes || [],
        });

        this.setState({eventContentTypes: this.props.formParams.eventContentTypes || []});

        const preselectedPropertyLicence = this.props.formValues.property_licence
            || (1 === (this.props.formParams.propertyLicencesOptions || []).length
                ? this.props.formParams.propertyLicencesOptions[0].value
                : null);

        if (preselectedPropertyLicence && !_isEmpty(this.props.formParams.matches)) {
            this.prepareCoverageField(this.props.formParams.matches, preselectedPropertyLicence);
        }
    }

    componentDidUpdate(prevProps, prevState) {
        prefillCoverage(
            _get(this.state, 'defaultForm_fields.coverage.props.hidden'),
            this.state.selectedPresetData?.event?.products,
            _get(this.state, 'defaultForm_fields.coverage.props.options', []),
            this.setField,
            this.state.commonProducts,
            (newState) => this.setState(newState)
        );

        const eventPresetChanged = this.props.formValues.event_preset !== prevProps.formValues.event_preset;
        let nextFormValues = this.props.formValues;

        if (eventPresetChanged && this.props.change) {
            const presetProducts = this.state.selectedPresetData?.event?.products;
            const hasPresetProductsLoaded = Array.isArray(presetProducts);
            const availableCoverageProducts = _get(this.state, 'defaultForm_fields.coverage.props.options', []);
            const selectedPresetProductIds = (presetProducts || []).map((product) => Number(product.id));
            const syncedCoverage = availableCoverageProducts
                .filter((option) => selectedPresetProductIds.includes(Number(option.id ?? option.value)))
                .map((option) => Number(option.value))
                .filter((coverageId) => !Number.isNaN(coverageId));

            if (hasPresetProductsLoaded && !_isEqual(this.props.formValues.coverage || [], syncedCoverage)) {
                this.props.change('coverage', syncedCoverage);
                this.setField('coverage', {
                    defaultValue: syncedCoverage,
                    timestamp: Date.now(),
                });
            }

            if (hasPresetProductsLoaded && 0 === syncedCoverage.length && !_isEmpty(this.props.formValues.clip_providers)) {
                this.props.change('clip_providers', {});
            }

            if (hasPresetProductsLoaded) {
                nextFormValues = {
                    ...nextFormValues,
                    coverage: syncedCoverage,
                    clip_providers: 0 === syncedCoverage.length ? {} : nextFormValues.clip_providers,
                };
            }
        }

        const currentProps = nextFormValues === this.props.formValues
            ? this.props
            : {...this.props, formValues: nextFormValues};
        const coverage = nextFormValues.coverage || [];
        const previousCoverage = prevProps.formValues.coverage || [];
        const hasCoverage = 0 < coverage.length;
        const coverageChanged = !_isEqual(coverage, previousCoverage);
        const coverageKey = (coverage || []).join('|');
        const isClipProvidersFieldHidden = _get(
            this.state,
            'defaultForm_fields.clip_providers.props.hidden',
            _get(this.props, 'Model.fields.clip_providers.props.hidden', true)
        );

        if (coverageChanged) {
            this.lastClipProvidersCoverageKey = null;
        }

        const shouldBootstrapVisible = hasCoverage
            && isClipProvidersFieldHidden
            && coverageKey !== this.lastClipProvidersCoverageKey;

        if (!this.state.presetApplied && (coverageChanged || shouldBootstrapVisible)) {
            if (!hasCoverage) {
                this.setField('clip_providers', {
                    hidden: true,
                });
                this.lastClipProvidersCoverageKey = null;
            } else {
                const defaultClipProvider = getClipProviderFromPropertyLicence(this.state.rightsScopes, this.state.coverageProducts);
                const formClipProviders = normalizeClipProvidersValues(this.props.formValues.clip_providers ?? {});
                const fallbackDefaults = getDefaultClipProvidersValues(
                    this.props.formParams.eventContentTypes || [],
                    defaultClipProvider ? String(defaultClipProvider) : ''
                );
                const defaultDropdownValues = resolveClipProvidersDefaults(formClipProviders, fallbackDefaults);

                this.setField('clip_providers', {
                    hidden: false,
                    defaultDropdownValues,
                });
                setClipProvidersInForm(
                    this.props.change,
                    this.props.formParams.eventContentTypes || [],
                    defaultDropdownValues
                );
                this.lastClipProvidersCoverageKey = coverageKey;
            }
        }

        if (eventPresetChanged) {
            this.setState({ presetApplied: !!this.props.formValues.event_preset });
        }

        const didPresetsCacheBecomeAvailable = _isEmpty(prevState?.presetsListObject)
            && !_isEmpty(this.state.presetsListObject)
            && !!this.props.formValues.event_preset;

        if (didPresetsCacheBecomeAvailable) {
            updateClipProvidersFieldBasedOnPreset(
                currentProps,
                {
                    ...prevProps.formValues,
                    event_preset: null,
                },
                this.setField,
                this.state,
                this.state.presetsListObject,
                {setFormValue: this.props.change}
            );
        }

        updateClipProvidersFieldBasedOnPreset(
            currentProps,
            prevProps.formValues,
            this.setField,
            this.state,
            this.state.presetsListObject,
            {setFormValue: this.props.change}
        );
    }

    prepareDataForSubmit(data) {
        let dataForSubmit = super.prepareDataForSubmit(data).EventMultipleCreation;
        const hasCoverage = 0 < (data.coverage || []).length;
        const hasArgos = hasArgosJobScheduling(
            this.props.formParams.propertyLicences,
            data.property_licence
        );
        const hasSpiideo = hasSpiideoIntegration(
            this.props.formParams.propertyLicences,
            data.property_licence
        );

        dataForSubmit = Object.assign(dataForSubmit, {
            color: data.color.substring(1),
            matches: this.props.formParams.matches,
            products: data.coverage,
            clip_providers: hasCoverage ? formatClipProvidersForSubmit(data) : [],
            has_argos_job_scheduling: !!(hasArgos && data.argos_job_scheduling),
            has_spiideo_integration: !!(hasSpiideo && data.spiideo_integration),
        });

        return dataForSubmit;
    }

    prepareCoverageField(matchIds, propertyLicenceId) {
        getRightScopesWithMediaRightsForMatches(matchIds, propertyLicenceId)
            .then(data => {
                const coverageProducts = getCommonProductsWithBookingSupport(data.rightsScopesForMatches);
                this.setState({
                    coverageProducts: coverageProducts,
                    rightsScopes: data.rightsScopesForMatches,
                });

                this.setField('coverage', {
                    options: transformProductsForCoverageDropdown(coverageProducts),
                    hidden: !coverageProducts.length,
                });
            });
    }
}

export default withApollo(EventsMultipleCreationForm);
